import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Checkbox, Chip, TextField } from "@mui/material";
import { surfaceDefectChipSx } from "../components/SurfaceDefectChip";
import { useQuery } from "@tanstack/react-query";
import { FieldError } from "react-hook-form";
import { SurfaceDefect } from "../models/Inspection";
import { getSurfaceDefects } from "../repositories/SurfaceDefectRepository";

export const useSurfaceDefects = (includeInactive = false) =>
	useQuery<SurfaceDefect[], Error>({
		queryKey: ["surface-defects", includeInactive],
		queryFn: ({ signal }) => getSurfaceDefects({ includeInactive }, signal),
		staleTime: 300_000,
	});

type Props = {
	label?: string;
	value: number[];
	onChange?: (value: number[], defects: SurfaceDefect[]) => void;
	disabled?: boolean;
	fieldError?: FieldError;
};

export const SurfaceDefectSelector = ({
	label = "Surface Defects",
	value,
	onChange = () => {},
	disabled = false,
	fieldError,
}: Props) => {
	const { data, isLoading } = useSurfaceDefects();

	const defects = data ?? [];
	const selected = value
		.map((id) => defects.find((defect) => defect.SurfaceDefectId === id))
		.filter((defect): defect is SurfaceDefect => !!defect);

	const allSelected = defects.length > 0 && selected.length === defects.length;

	return (
		<Autocomplete
			multiple
			disableCloseOnSelect
			size="small"
			disabled={disabled || isLoading}
			options={defects}
			value={selected}
			getOptionLabel={(option) => option.SurfaceDefectName}
			isOptionEqualToValue={(option, item) =>
				option.SurfaceDefectId === item.SurfaceDefectId
			}
			onChange={(_event, items) =>
				onChange(
					items.map((item) => item.SurfaceDefectId as number),
					items,
				)
			}
			renderOption={(props, option, { selected: isSelected }) => {
				const { key, ...liProps } = props as typeof props & { key: string };

				return (
					<li key={key} {...liProps}>
						<Checkbox
							size="small"
							icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
							checkedIcon={<CheckBoxIcon fontSize="small" />}
							checked={isSelected}
							sx={{ mr: 1, p: 0.25 }}
						/>
						<Chip
							size="small"
							label={option.SurfaceDefectName}
							sx={surfaceDefectChipSx(option.Color)}
						/>
					</li>
				);
			}}
			renderTags={(items, getTagProps) => {
				if (allSelected)
					return (
						<Chip
							size="small"
							label="Any defect"
							onDelete={() => onChange([], [])}
							sx={{ fontWeight: 600 }}
						/>
					);

				return items.map((item, index) => {
					const { key, ...tagProps } = getTagProps({ index });

					return (
						<Chip
							key={key}
							size="small"
							label={item.SurfaceDefectName}
							{...tagProps}
							sx={surfaceDefectChipSx(item.Color)}
						/>
					);
				});
			}}
			sx={{
				"& .MuiAutocomplete-inputRoot": { maxHeight: 62, overflowY: "auto" },
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					error={!!fieldError}
					helperText={fieldError?.message}
					inputProps={{
						...params.inputProps,
						"data-test-id": "surface-defect-selector",
					}}
					sx={{
						backgroundColor: "white",
					}}
				/>
			)}
		/>
	);
};
